import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotify } from '../context/NotificationContext';

const FIELD_INPUT = 'w-full flex flex-row items-center justify-start px-[14px] py-[10px] text-[13px] text-[#111827] placeholder-[#9CA3AF] bg-[#F9F9F9] border border-[#E5E7EB] rounded-[10px] outline-none focus:border-[#A9A9A9] focus:bg-white transition-colors';

// Two-step flow:
//   1. User submits email → Cognito mails a 6-digit code
//   2. User submits code + new password → Cognito sets the new password
function ForgotPasswordPage() {
    const { forgotPassword, confirmForgotPassword } = useAuth();
    const navigate = useNavigate();
    const { notifyError } = useNotify() || {};

    const [step, setStep]               = useState(1);
    const [email, setEmail]             = useState('');
    const [code, setCode]               = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNew, setConfirmNew]   = useState('');
    const [info, setInfo]               = useState('');
    const [loading, setLoading]         = useState(false);

    async function submitStep1(e) {
        e.preventDefault();
        setInfo('');
        if (!email.trim()) { notifyError?.('Enter your email.'); return; }
        setLoading(true);
        const res = await forgotPassword(email);
        setLoading(false);
        if (res.ok) {
            setStep(2);
            setInfo('If an account exists for that email, we sent a 6-digit code. Check your inbox.');
        } else {
            notifyError?.(res.error || 'Could not send reset code.');
        }
    }

    async function submitStep2(e) {
        e.preventDefault();
        setInfo('');
        if (!code.trim())                            { notifyError?.('Enter the 6-digit code from your email.'); return; }
        if (!newPassword || newPassword.length < 8)  { notifyError?.('New password must be at least 8 characters.'); return; }
        if (newPassword !== confirmNew)              { notifyError?.('Passwords do not match.'); return; }
        setLoading(true);
        const res = await confirmForgotPassword(email, code, newPassword);
        setLoading(false);
        if (res.ok) {
            navigate('/login', { replace: true, state: { resetSuccess: true } });
        } else {
            notifyError?.(res.error || 'Could not reset password.');
        }
    }

    return (
        <div className="w-screen min-h-screen flex flex-col items-center justify-center px-4 bg-[#F9F9F9]">
            <div className="w-full max-w-[400px] flex flex-col items-stretch justify-start">

                <div className="w-full flex flex-col items-start justify-start gap-y-1 mb-7">
                    <h1 className="text-[20px] font-semibold text-[#111827]">Reset your password</h1>
                    <p className="text-[13px] text-[#9CA3AF]">
                        {step === 1
                            ? 'Enter the email on your account and we’ll send you a code.'
                            : 'Enter the code we sent and pick a new password.'}
                    </p>
                </div>

                <div className="w-full flex flex-col items-stretch justify-start px-8 py-8 bg-white border border-[#F3F4F6] rounded-[16px] shadow-sm">
                    <form onSubmit={step === 1 ? submitStep1 : submitStep2} className="w-full flex flex-col items-stretch justify-start gap-4">

                        <div className="w-full flex flex-col items-start justify-start gap-y-[6px]">
                            <label className="text-[12px] font-medium text-[#374151]">Email</label>
                            <input
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={step === 2}
                                placeholder="you@company.com"
                                className={FIELD_INPUT + ' disabled:opacity-70'}
                            />
                        </div>

                        {step === 2 && (
                            <>
                                <div className="w-full flex flex-col items-start justify-start gap-y-[6px]">
                                    <label className="text-[12px] font-medium text-[#374151]">Verification code</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                        placeholder="6-digit code"
                                        maxLength={6}
                                        className={FIELD_INPUT + ' font-mono'}
                                    />
                                </div>

                                <div className="w-full flex flex-col items-start justify-start gap-y-[6px]">
                                    <label className="text-[12px] font-medium text-[#374151]">New password</label>
                                    <input
                                        type="password"
                                        autoComplete="new-password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Min 8 chars, upper, lower, number"
                                        className={FIELD_INPUT}
                                    />
                                </div>

                                <div className="w-full flex flex-col items-start justify-start gap-y-[6px]">
                                    <label className="text-[12px] font-medium text-[#374151]">Confirm new password</label>
                                    <input
                                        type="password"
                                        autoComplete="new-password"
                                        value={confirmNew}
                                        onChange={(e) => setConfirmNew(e.target.value)}
                                        placeholder="Repeat new password"
                                        className={FIELD_INPUT}
                                    />
                                </div>
                            </>
                        )}

                        {info && (
                            <p className="w-full px-3 py-2 text-[12px] text-[#16A34A] bg-[#F0FDF4] border border-[#86EFAC] rounded-[8px]">{info}</p>
                        )}
                        {/* Errors are toasts, never inline (owner, 2026-10-20). */}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex flex-row items-center justify-center mt-1 py-[11px] text-[13px] font-semibold text-white bg-[#1A1A1A] rounded-[10px] cursor-pointer hover:bg-black transition-colors duration-150 disabled:opacity-60"
                        >
                            {loading
                                ? (step === 1 ? 'Sending…' : 'Resetting…')
                                : (step === 1 ? 'Send code' : 'Reset password')}
                        </button>

                        <div className="w-full flex flex-row items-center justify-center">
                            <Link to="/login" className="text-[12px] text-[#9CA3AF] hover:text-[#374151] transition-colors">
                                ← Back to sign in
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ForgotPasswordPage;
